// Product gallery: thumbnails and arrows scroll the track via commands (--goto, --prev, --next);
// an IntersectionObserver keeps the active thumbnail and the mobile counter in sync.
// With [data-hide-variant-media], items whose data-media-variants list excludes the selected
// variant get [data-media-hidden] (CSS removes them and their thumbnails). The selected variant
// is read from the product form's input[name="id"], which s-variant-picker syncs and re-renders.
class ProductGallery extends HTMLElement {
  #controller = null;
  #observer = null;
  #formObserver = null;
  #items = [];
  #active = 0;
  #variantId = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;
    this.#items = [...track.querySelectorAll('[data-gallery-item]')];

    this.addEventListener('command', (event) => this.#onCommand(event), { signal: this.#controller.signal });

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setActive(this.#items.indexOf(entry.target));
          }
        }
      },
      { root: track, threshold: [0.6] }
    );
    for (const item of this.#items) this.#observer.observe(item);

    track.addEventListener('scroll', () => this.#syncPaging(track), { passive: true, signal: this.#controller.signal });
    this.#syncPaging(track);
    if (this.hasAttribute('data-hide-variant-media')) this.#watchVariant();
    this.#setActive(this.#visibleItems().length ? this.#items.indexOf(this.#visibleItems()[0]) : 0);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#formObserver?.disconnect();
  }

  #watchVariant() {
    const form = document.querySelector('[data-product-form]');
    if (!form) return;
    this.#variantId = this.#readVariant(form);
    const check = () => {
      const id = this.#readVariant(form);
      if (id && id !== this.#variantId) {
        this.#variantId = id;
        this.#filter(id);
      }
    };
    // Partial re-renders replace the hidden input (childList); option changes sync its value first.
    this.#formObserver = new MutationObserver(check);
    this.#formObserver.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    form.addEventListener('change', () => queueMicrotask(check), { signal: this.#controller.signal });
  }

  #readVariant(form) {
    return form.querySelector('input[name="id"]')?.value || '';
  }

  #filter(variantId) {
    const withMedia = (this.dataset.variantsWithMedia || '').split(' ').filter(Boolean);
    // A variant without its own media shows the whole gallery rather than an empty one.
    const showAll = !withMedia.includes(variantId);
    this.#items.forEach((item, index) => {
      const owners = item.dataset.mediaVariants || 'all';
      const show = showAll || owners === 'all' || owners.split(' ').includes(variantId);
      item.toggleAttribute('data-media-hidden', !show);
      const thumb = this.querySelector(`[data-thumb-index="${index}"]`);
      if (thumb) thumb.toggleAttribute('data-media-hidden', !show);
    });
    const total = this.querySelector('[data-gallery-total]');
    if (total) total.textContent = String(this.#visibleItems().length);

    const first = this.#visibleItems()[0];
    if (first) {
      const track = this.querySelector('[data-gallery-track]');
      if (track && track.scrollWidth > track.clientWidth + 1) track.scrollTo({ left: 0, behavior: 'auto' });
      this.#setActive(this.#items.indexOf(first));
    }
  }

  #visibleItems() {
    return this.#items.filter((item) => !item.hasAttribute('data-media-hidden') || !this.hasAttribute('data-hide-variant-media'));
  }

  #onCommand(event) {
    const track = this.querySelector('[data-gallery-track]');
    // Row grids (column flow) page sideways by one view instead of stepping item by item.
    if (track && (event.command === '--next' || event.command === '--prev') && getComputedStyle(track).gridAutoFlow.startsWith('column')) {
      const isRtl = getComputedStyle(track).direction === 'rtl';
      const step = (event.command === '--next' ? 1 : -1) * (isRtl ? -1 : 1);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: step * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      return;
    }
    const visible = this.#visibleItems();
    const position = Math.max(visible.indexOf(this.#items[this.#active]), 0);
    let target;
    if (event.command === '--goto') target = this.#items[Number(event.source?.dataset.index ?? 0)];
    else if (event.command === '--next') target = visible[Math.min(position + 1, visible.length - 1)];
    else if (event.command === '--prev') target = visible[Math.max(position - 1, 0)];
    else return;
    if (target) this.#scrollTo(this.#items.indexOf(target));
  }

  #scrollTo(index) {
    const item = this.#items[index];
    if (!item) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 'nearest' keeps vertical layouts from jumping and is direction-agnostic for horizontal (RTL-safe) tracks.
    item.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  // Row grids: arrows reflect whether the track can scroll further either way.
  #syncPaging(track) {
    if (!getComputedStyle(track).gridAutoFlow.startsWith('column')) return;
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    this.querySelector('[command="--prev"]')?.toggleAttribute('disabled', pos <= 1);
    this.querySelector('[command="--next"]')?.toggleAttribute('disabled', pos >= max - 1);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#active = index;
    for (const thumb of this.querySelectorAll('[data-gallery-thumbs] [data-index]')) {
      const current = Number(thumb.dataset.index) === index;
      thumb.toggleAttribute('data-active', current);
      if (current) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
    const visible = this.#visibleItems();
    const position = visible.indexOf(this.#items[index]);
    const counter = this.querySelector('[data-gallery-current]');
    if (counter) counter.textContent = String(position + 1);
    const track = this.querySelector('[data-gallery-track]');
    if (track && getComputedStyle(track).gridAutoFlow.startsWith('column')) {
      this.#syncPaging(track);
      return;
    }
    const prev = this.querySelector('[command="--prev"]');
    const next = this.querySelector('[command="--next"]');
    if (prev) prev.toggleAttribute('disabled', position <= 0);
    if (next) next.toggleAttribute('disabled', position === visible.length - 1);
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', ProductGallery);
}
